import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Mail, Truck, ShieldCheck, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { apiGetOrder } from "../lib/api";

const OrderConfirmation = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGetOrder(orderNumber).then(setOrder).catch(() => setError("Order not found"));
  }, [orderNumber]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="order-not-found">
        <h1 className="font-display text-3xl font-semibold text-[#0a0a0a]">Order not found</h1>
        <p className="text-gray-500 mt-2 font-light">We couldn't locate that order. Please check the order number.</p>
        <Button asChild className="btn-primary rounded-none mt-6 h-11 px-6 text-[12px] font-semibold uppercase tracking-[0.18em]">
          <Link to="/shop">Continue shopping</Link>
        </Button>
      </div>
    );
  }
  if (!order) {
    return <div className="max-w-2xl mx-auto px-4 py-24 text-center text-sm text-gray-500">Loading order…</div>;
  }

  return (
    <div className="max-w-[1100px] mx-auto px-4 lg:px-10 py-16" data-testid="order-confirmation-page">
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#0a0a0a] mb-6">
          <CheckCircle2 className="h-8 w-8 text-white" />
        </div>
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="h-px w-8 bg-[#b91c1c]" />
          <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Order Confirmed</span>
          <div className="h-px w-8 bg-[#b91c1c]" />
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#0a0a0a] tracking-tight">
          Thank you, <span className="font-serif italic font-normal">{order.firstName}.</span>
        </h1>
        <p className="text-gray-500 mt-4 font-light">
          Your order <strong className="text-[#0a0a0a] font-mono">{order.orderNumber}</strong> is confirmed.
          A receipt has been sent to <strong className="text-[#0a0a0a]">{order.email}</strong>.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
        <div className="space-y-8">
          <section className="bg-white border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold text-[#0a0a0a]">Order Details</h2>
              <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#b91c1c]">{order.status}</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {order.items.map((i, idx) => (
                  <tr key={idx} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-3 flex items-center gap-3">
                      {i.image && <img src={i.image} alt={i.name} className="h-12 w-12 object-contain bg-white border border-gray-100" />}
                      <div>
                        <div className="font-semibold text-[#0a0a0a]">{i.name}</div>
                        <div className="text-xs text-gray-500">{i.size}</div>
                      </div>
                    </td>
                    <td className="py-3 text-right text-gray-500 text-xs">× {i.qty}</td>
                    <td className="py-3 text-right font-semibold">${(i.price * i.qty).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-5 pt-4 border-t border-gray-200 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
              {order.discount > 0 && (
                <div className="flex justify-between text-[#b91c1c]">
                  <span>Discount {order.couponCode ? `(${order.couponCode})` : ""}</span>
                  <span>-${order.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span>{order.shipping === 0 ? "FREE" : `$${order.shipping.toFixed(2)}`}</span></div>
              <div className="flex justify-between text-base font-bold pt-3 border-t border-gray-100 mt-2">
                <span>Total</span><span className="text-[#b91c1c]">${order.total.toFixed(2)}</span>
              </div>
            </div>
          </section>

          <section className="bg-white border border-gray-200 p-6">
            <h2 className="font-display text-xl font-semibold text-[#0a0a0a] mb-4">Shipping To</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong className="text-[#0a0a0a]">{order.firstName} {order.lastName}</strong><br />
              {order.address}<br />
              {order.city}, {order.state} {order.zip}<br />
              {order.country}
            </p>
          </section>
        </div>

        <aside className="space-y-4 h-fit">
          <div className="bg-[#0a0a0a] text-white p-6">
            <div className="text-[10px] font-semibold tracking-[0.32em] uppercase text-red-400 mb-3">What's next</div>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3"><Mail className="h-4 w-4 text-red-400 shrink-0 mt-0.5" /><span>Receipt sent to your inbox.</span></li>
              <li className="flex gap-3"><Truck className="h-4 w-4 text-red-400 shrink-0 mt-0.5" /><span>Ships within 24 hours from our American facility.</span></li>
              <li className="flex gap-3"><ShieldCheck className="h-4 w-4 text-red-400 shrink-0 mt-0.5" /><span>Tracked, discreet, temperature-controlled delivery.</span></li>
              <li className="flex gap-3"><FileText className="h-4 w-4 text-red-400 shrink-0 mt-0.5" /><span>COA for your batch will be available on your product page.</span></li>
            </ul>
          </div>
          <Button asChild className="w-full btn-primary rounded-none h-12 text-[12px] font-semibold uppercase tracking-[0.18em]">
            <Link to="/shop" data-testid="continue-shopping-btn">Continue Shopping</Link>
          </Button>
          <Button asChild variant="outline" className="w-full rounded-none h-12 border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white text-[12px] font-semibold uppercase tracking-[0.18em]">
            <Link to="/contact">Contact Support</Link>
          </Button>
          <p className="text-[11px] text-gray-400 text-center">
            Bookmark this page or save your order number to track later.
          </p>
        </aside>
      </div>
    </div>
  );
};

export default OrderConfirmation;
